import { useState } from "react";
import { BrainSaveButton, BrainHistory, useBrainHistory } from "@/components/BrainHistory";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Brain, Loader2, Sparkles, ShieldAlert, Stethoscope, ArrowRightCircle, AlertTriangle } from "lucide-react";

const CONF = { high: "bg-emerald-100 text-emerald-700", moderate: "bg-amber-100 text-amber-700", low: "bg-slate-200 text-slate-600" };

export default function ClinicalBrainPanel({ clinikoId, onInsert }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [history, reloadHistory] = useBrainHistory(clinikoId);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/clinical-brain/${clinikoId}`);
      setData(res.data.suggestions);
      setSavedId(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Clinical Brain unavailable — retry"); }
    finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="clinical-brain-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-600 text-white"><Brain className="h-5 w-5" /></div>
          <div>
            <div className="font-heading text-sm font-bold text-slate-900">Clinical Brain</div>
            <div className="text-[11px] text-indigo-600">AI second opinion · suggestions only</div>
          </div>
        </div>
        <Button onClick={run} disabled={loading || !clinikoId} data-testid="clinical-brain-suggest-btn" className="bg-indigo-600 hover:bg-indigo-700">
          {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
          Suggest next steps
        </Button>
      </div>

      <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-[11px] font-medium text-amber-700">
        <ShieldAlert className="h-3.5 w-3.5" /> Suggestions only — clinician confirms. Nothing is auto-applied.
      </div>

      {loading && <div className="mt-6 flex flex-col items-center gap-2 py-6 text-sm text-indigo-500"><Loader2 className="h-6 w-6 animate-spin" /> Analysing notes & history...</div>}

      {data && !loading && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Assessment</span>
            <div className="flex items-center gap-2">
              {data.confidence && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${CONF[data.confidence] || CONF.low}`}>{data.confidence} confidence</span>}
              <BrainSaveButton clinikoId={clinikoId} data={data} saved={!!savedId} onSaved={(rec) => { setSavedId(rec.id); reloadHistory(); }} />
            </div>
          </div>

          <Card icon={Stethoscope} title="Likely impression" body={data.likely_diagnosis} />
          <Card icon={ArrowRightCircle} title="Suggested next phase" body={data.next_phase} />

          {data.treatment_ideas?.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-2 text-sm font-semibold text-slate-700">Treatment ideas</div>
              <div className="space-y-2">
                {data.treatment_ideas.map((t, i) => (
                  <div key={t.idea || i} className="rounded-lg bg-slate-50 p-2.5" data-testid={`brain-idea-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-slate-800">{t.idea}</div>
                      {onInsert && <button onClick={() => onInsert(t)} data-testid={`brain-insert-${i}`} className="flex-shrink-0 rounded-md bg-indigo-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-indigo-700">Insert into Plan</button>}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{t.rationale}</div>
                    {t.references?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {t.references.map((r) => <span key={r.id} className="rounded bg-teal-50 px-1.5 py-0.5 font-mono text-[10px] text-teal-700">[{r.id}] {r.citation}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.red_flags?.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3">
              <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-red-700"><AlertTriangle className="h-4 w-4" /> Screen / rule out</div>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-red-600">{data.red_flags.map((f, i) => <li key={`${i}-${f}`}>{f}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {!data && !loading && <div className="mt-6 py-6 text-center text-sm text-indigo-400">Run Clinical Brain to get evidence-cited next steps.</div>}
      <BrainHistory items={history} onOpen={(it) => { setData(it.suggestions); setSavedId(it.id); }} />
    </div>
  );
}

const Card = ({ icon: Icon, title, body }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Icon className="h-4 w-4 text-indigo-600" /> {title}</div>
    <p className="mt-1 text-sm text-slate-600">{body}</p>
  </div>
);
